import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Breadcrumb,
  Button,
  Col,
  Container,
  Form,
  ListGroup,
  Nav,
  Offcanvas,
  Row,
} from 'react-bootstrap';
import PageTitle from '../components/PageTitle';
import './DocumentationPage.css';

const DOC_SECTIONS = [
  {
    id: 'overview',
    title: 'Overview',
    headings: [
      { id: 'why-medhive', title: 'Why MedHive?' },
      { id: 'what-covers', title: 'What This Documentation Covers' },
      { id: 'where-start', title: 'Where To Start' },
    ],
  },
  {
    id: 'getting-started',
    title: 'Getting Started',
    headings: [
      { id: 'create-account', title: 'Creating Your Account' },
      { id: 'program-setup', title: 'Program Setup' },
      { id: 'invite-users', title: 'Inviting Users' },
    ],
  },
  {
    id: 'rotation-scheduling',
    title: 'Rotation Scheduling',
    headings: [
      { id: 'planning-rotations', title: 'Planning Rotations' },
      { id: 'call-schedules', title: 'Call Schedules' },
      { id: 'daily-view', title: 'Daily Schedule View' },
    ],
  },
  {
    id: 'document-onboarding',
    title: 'Document Onboarding',
    headings: [
      { id: 'form-builder', title: 'Building Forms' },
      { id: 'assigning-documents', title: 'Assigning Documents' },
      { id: 'tracking-completion', title: 'Tracking Completion' },
    ],
  },
  {
    id: 'conference-scheduling',
    title: 'Conference Scheduling',
    headings: [
      { id: 'creating-conferences', title: 'Creating Conferences' },
      { id: 'attendance', title: 'Attendance Tracking' },
    ],
  },
  {
    id: 'evaluations',
    title: 'Evaluations',
    headings: [
      { id: 'evaluation-templates', title: 'Evaluation Templates' },
      { id: 'completing-evaluations', title: 'Completing Evaluations' },
      { id: 'viewing-results', title: 'Viewing Results' },
    ],
  },
  {
    id: 'work-hours',
    title: 'Work Hour Logging',
    headings: [
      { id: 'logging-hours', title: 'Logging Hours' },
      { id: 'director-review', title: 'Director Review' },
      { id: 'compliance', title: 'Compliance Monitoring' },
    ],
  },
  {
    id: 'user-roles',
    title: 'User Roles',
    headings: [
      { id: 'role-overview', title: 'Role Overview' },
      { id: 'permissions', title: 'Permissions' },
    ],
  },
  {
    id: 'notifications',
    title: 'Alerts & Notifications',
    headings: [
      { id: 'notification-types', title: 'Notification Types' },
      { id: 'managing-alerts', title: 'Managing Alerts' },
    ],
  },
];

const SECTION_CONTENT = {
  overview: (
    <>
      <p className="text-body-secondary">
        MedHive is a comprehensive residency management platform designed for program directors,
        coordinators, faculty, and residents. It brings rotation scheduling, evaluations, work hour
        tracking, document onboarding, and conference management into a single unified system —
        replacing scattered spreadsheets, emails, and paper workflows.
      </p>
      <h2 id="why-medhive" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Why MedHive?</h2>
      <p>
        Residency programs juggle dozens of moving parts: rotation assignments, call schedules,
        evaluation deadlines, ACGME compliance, and onboarding paperwork. MedHive centralizes all
        of this so program leadership can focus on education rather than administration.
      </p>
      <p>
        Built specifically for graduate medical education, MedHive understands the unique workflows
        of residency programs — from block-based rotations to milestone evaluations and duty hour
        limits.
      </p>
      <h2 id="what-covers" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">What This Documentation Covers</h2>
      <p>This documentation walks you through every major feature of MedHive:</p>
      <ol>
        <li><Link to="/documentation/getting-started">Getting Started</Link> — account setup and initial program configuration</li>
        <li><Link to="/documentation/rotation-scheduling">Rotation Scheduling</Link> — plan rotations, call schedules, and daily assignments</li>
        <li><Link to="/documentation/document-onboarding">Document Onboarding</Link> — build forms, assign documents, and track completion</li>
        <li><Link to="/documentation/conference-scheduling">Conference Scheduling</Link> — manage conferences and attendance</li>
        <li><Link to="/documentation/evaluations">Evaluations</Link> — create templates, collect feedback, and review results</li>
        <li><Link to="/documentation/work-hours">Work Hour Logging</Link> — log hours, review submissions, and monitor compliance</li>
        <li><Link to="/documentation/user-roles">User Roles</Link> — understand permissions for each role in your program</li>
        <li><Link to="/documentation/notifications">Alerts &amp; Notifications</Link> — stay on top of deadlines and program updates</li>
      </ol>
      <h2 id="where-start" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Where To Start</h2>
      <p>
        If you are setting up MedHive for the first time, begin with{' '}
        <Link to="/documentation/getting-started">Getting Started</Link> to create your program
        and invite users. Program directors should then configure{' '}
        <Link to="/documentation/rotation-scheduling">Rotation Scheduling</Link> and{' '}
        <Link to="/documentation/evaluations">Evaluations</Link> before the academic year begins.
      </p>
      <p>
        Residents can jump directly to the sections relevant to their daily workflow — work hour
        logging, viewing rotation schedules, and completing assigned evaluations or onboarding
        documents.
      </p>
    </>
  ),
  'getting-started': (
    <>
      <p className="text-body-secondary">
        This guide covers the essential first steps to get your residency program up and running
        on MedHive.
      </p>
      <h2 id="create-account" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Creating Your Account</h2>
      <p>
        Visit the <Link to="/signup">sign-up page</Link> to create an institutional account.
        You will need to provide your program name, institution, and administrator contact
        information. Once registered, you can log in and access the program dashboard.
      </p>
      <h2 id="program-setup" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Program Setup</h2>
      <p>
        After logging in, navigate to your program settings to configure basic details: program
        name, academic year dates, rotation blocks, and evaluation periods. Define your rotation
        sites and services so they are available when building schedules.
      </p>
      <h2 id="invite-users" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Inviting Users</h2>
      <p>
        From the Users page, invite residents, faculty, and staff by email. Each user is assigned
        a role that determines their access level. See{' '}
        <Link to="/documentation/user-roles">User Roles</Link> for a full breakdown of what each
        role can do.
      </p>
    </>
  ),
  'rotation-scheduling': (
    <>
      <p className="text-body-secondary">
        MedHive provides comprehensive rotation scheduling with drag-and-drop editing, automated
        call schedules, and calendar integration for residents.
      </p>
      <h2 id="planning-rotations" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Planning Rotations</h2>
      <p>
        Plan monthly, weekly, or block-based rotations with customizable durations and assignments.
        Use the rotation scheduler to assign residents to services, set start and end dates, and
        visualize the full academic year at a glance.
      </p>
      <h2 id="call-schedules" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Call Schedules</h2>
      <p>
        Automate weekend and 24-hour call schedules using advanced algorithms. Once generated,
        customize assignments with drag-and-drop. Call schedules sync with the daily view so
        residents always know who is on call.
      </p>
      <h2 id="daily-view" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Daily Schedule View</h2>
      <p>
        The daily schedule tab shows assignments for all residents on a given day. Program
        directors can quickly see coverage gaps, make adjustments, and export schedules for
        distribution.
      </p>
    </>
  ),
  'document-onboarding': (
    <>
      <p className="text-body-secondary">
        Streamline resident onboarding with customizable document forms, e-signatures, and
        completion tracking.
      </p>
      <h2 id="form-builder" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Building Forms</h2>
      <p>
        Use the form builder to create onboarding documents with text fields, checkboxes, file
        uploads, and signature fields. Templates can be reused across cohorts and updated as
        requirements change.
      </p>
      <h2 id="assigning-documents" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Assigning Documents</h2>
      <p>
        Assign forms to individual residents or entire cohorts. Set due dates and send reminders
        automatically. Residents receive notifications when new documents are assigned.
      </p>
      <h2 id="tracking-completion" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Tracking Completion</h2>
      <p>
        Monitor completion status from the onboarding dashboard. Filter by resident, document
        type, or status to identify outstanding items before deadlines.
      </p>
    </>
  ),
  'conference-scheduling': (
    <>
      <p className="text-body-secondary">
        Manage didactic conferences, journal clubs, and other educational sessions with built-in
        scheduling and attendance tools.
      </p>
      <h2 id="creating-conferences" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Creating Conferences</h2>
      <p>
        Create recurring or one-time conference events with title, date, location, and presenter
        details. Residents and faculty can view the conference calendar from their dashboards.
      </p>
      <h2 id="attendance" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Attendance Tracking</h2>
      <p>
        Track attendance via QR codes or manual check-in. Generate attendance reports for
        accreditation documentation and identify residents with attendance gaps.
      </p>
    </>
  ),
  evaluations: (
    <>
      <p className="text-body-secondary">
        MedHive supports the full evaluation lifecycle — from template creation to faculty
        completion and aggregated results review.
      </p>
      <h2 id="evaluation-templates" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Evaluation Templates</h2>
      <p>
        Build evaluation forms with customizable questions, rating scales, and narrative fields.
        Templates can be tied to specific rotations or evaluation periods and assigned to faculty
        evaluators automatically.
      </p>
      <h2 id="completing-evaluations" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Completing Evaluations</h2>
      <p>
        Faculty receive notifications when evaluations are due. They can complete forms from
        their dashboard, with progress saved automatically. Residents can also complete
        self-evaluations where configured.
      </p>
      <h2 id="viewing-results" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Viewing Results</h2>
      <p>
        Program directors access aggregated evaluation results, individual response details, and
        performance trends over time. Export data for CCC meetings and milestone reviews.
      </p>
    </>
  ),
  'work-hours': (
    <>
      <p className="text-body-secondary">
        Track resident duty hours to ensure ACGME compliance and identify potential violations
        before they become issues.
      </p>
      <h2 id="logging-hours" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Logging Hours</h2>
      <p>
        Residents log daily work hours including clinical time, didactics, and call shifts. The
        interface supports quick entry and shows running totals against weekly limits.
      </p>
      <h2 id="director-review" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Director Review</h2>
      <p>
        Program directors review submitted logs, approve or flag entries, and view monthly
        summaries per resident. Bulk review tools help process large programs efficiently.
      </p>
      <h2 id="compliance" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Compliance Monitoring</h2>
      <p>
        Automated alerts notify directors and residents when hours approach or exceed ACGME limits.
        Compliance dashboards highlight trends and recurring issues across the program.
      </p>
    </>
  ),
  'user-roles': (
    <>
      <p className="text-body-secondary">
        MedHive uses role-based access control so each user sees the tools and data relevant to
        their responsibilities.
      </p>
      <h2 id="role-overview" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Role Overview</h2>
      <ul>
        <li><strong>Program Director</strong> — full access to scheduling, evaluations, work logs, users, and settings</li>
        <li><strong>Coordinator / Admin</strong> — manage schedules, onboarding, and user accounts</li>
        <li><strong>Faculty / Attending</strong> — complete evaluations, view assigned residents</li>
        <li><strong>Resident</strong> — view schedule, log hours, complete evaluations and onboarding documents</li>
        <li><strong>Student</strong> — limited access for visiting or rotating medical students</li>
      </ul>
      <h2 id="permissions" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Permissions</h2>
      <p>
        Permissions are enforced at both the UI and API level. Directors can delegate specific
        responsibilities to coordinators without granting full administrative access. Contact{' '}
        <Link to="/contact">support</Link> if you need custom role configurations for your
        institution.
      </p>
    </>
  ),
  notifications: (
    <>
      <p className="text-body-secondary">
        Stay informed about deadlines, schedule changes, and program announcements through
        MedHive&apos;s notification system.
      </p>
      <h2 id="notification-types" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Notification Types</h2>
      <ul>
        <li>Evaluation due date reminders</li>
        <li>Onboarding document assignments</li>
        <li>Schedule change alerts</li>
        <li>Work hour compliance warnings</li>
        <li>Program-wide announcements</li>
      </ul>
      <h2 id="managing-alerts" className="h4 fw-bold mt-5 pt-2 border-bottom pb-2">Managing Alerts</h2>
      <p>
        Access all notifications from the bell icon in the navigation bar. Mark items as read,
        filter by type, and configure email delivery preferences in your profile settings.
      </p>
    </>
  ),
};

const DocsOnThisPage = ({ headings, activeHeading }) => (
  <aside className="docs-toc">
    <div className="small fw-semibold mb-3">On This Page</div>
    <Nav className="flex-column">
      {headings.map((h) => (
        <Nav.Link
          key={h.id}
          href={`#${h.id}`}
          className={`px-0 py-1 small ${activeHeading === h.id ? 'fw-medium text-body' : 'text-muted'}`}
          onClick={(e) => {
            e.preventDefault();
            document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        >
          {h.title}
        </Nav.Link>
      ))}
    </Nav>
    <hr className="my-3" />
    <Link to="/contact" className="small text-muted text-decoration-none">
      Questions or feedback? <i className="bi bi-box-arrow-up-right" />
    </Link>
  </aside>
);

const DocsSidebarNav = ({ activeSection, onNavigate }) => (
  <Nav className="flex-column">
    {DOC_SECTIONS.map((s) => (
      <Nav.Link
        key={s.id}
        as={Link}
        to={`/documentation/${s.id}`}
        className={`px-3 py-1 small ${activeSection === s.id ? 'fw-medium text-body' : 'text-muted'}`}
        onClick={onNavigate}
      >
        {s.title}
      </Nav.Link>
    ))}
  </Nav>
);

const DocumentationPage = () => {
  const { section: sectionParam } = useParams();
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const mainRef = useRef(null);

  const activeSection = sectionParam && SECTION_CONTENT[sectionParam] ? sectionParam : 'overview';
  const currentDoc = DOC_SECTIONS.find((s) => s.id === activeSection) || DOC_SECTIONS[0];

  const [bannerVisible, setBannerVisible] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [activeHeading, setActiveHeading] = useState('');
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('theme');
    return stored ? stored === 'dark' : false;
  });

  useEffect(() => {
    if (!sectionParam) {
      navigate('/documentation/overview', { replace: true });
      return;
    }
    if (!SECTION_CONTENT[sectionParam]) {
      navigate('/documentation/overview', { replace: true });
    }
  }, [sectionParam, navigate]);

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
    setActiveHeading('');
  }, [activeSection]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    document.body.setAttribute('data-bs-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setSearchFocused(false);
        setSearchQuery('');
        searchRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const root = mainRef.current;
    if (!root) return;

    const headings = currentDoc.headings.map((h) => h.id);
    const elements = headings
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) {
          setActiveHeading(visible[0].target.id);
        }
      },
      { root, rootMargin: '0px 0px -60% 0px', threshold: 0 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [currentDoc, activeSection]);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return DOC_SECTIONS.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.id.replace(/-/g, ' ').includes(q)
    );
  }, [searchQuery]);

  const handleSearchSelect = useCallback(
    (sectionId) => {
      setSearchQuery('');
      setSearchFocused(false);
      navigate(`/documentation/${sectionId}`);
    },
    [navigate]
  );

  const pageTitle = currentDoc.id === 'overview' ? 'MedHive' : currentDoc.title;

  return (
    <div className="docs-page d-flex flex-column vh-100 overflow-hidden">
      <PageTitle
        title={`Documentation – ${currentDoc.title}`}
        description="MedHive documentation for residency program management"
      />

      <nav className="navbar flex-shrink-0 main-page border-bottom px-3 py-2 bg-inverse">
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            className="d-lg-none"
            onClick={() => setSidebarOpen(true)}
            aria-label="Toggle navigation"
          >
            <i className="bi bi-list" />
          </Button>
          <Link to="/documentation/overview" className="navbar-brand d-flex align-items-center gap-2 mb-0">
            <img src={darkMode ? '/images/asset4.svg' : '/images/asset5.svg'} alt="MedHive" style={{ height: '16px' }} />
          </Link>
        </div>

        <div className="position-relative flex-grow-1 mx-3 mx-md-4" style={{ maxWidth: '420px' }}>
          <Form.Control
            ref={searchRef}
            type="search"
            size="sm"
            placeholder="Search documentation..."
            className="rounded-pill bg-light border-0 pe-5"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
          />

          {searchFocused && searchQuery.trim() && (
            <ListGroup className="position-absolute top-100 start-0 end-0 mt-1 shadow-sm z-3">
              {searchResults.length ? (
                searchResults.map((s) => (
                  <ListGroup.Item
                    key={s.id}
                    action
                    className="py-2"
                    onMouseDown={() => handleSearchSelect(s.id)}
                  >
                    <div className="small">{s.title}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {s.id.replace(/-/g, ' ')}
                    </div>
                  </ListGroup.Item>
                ))
              ) : (
                <ListGroup.Item className="text-muted small">No results found</ListGroup.Item>
              )}
            </ListGroup>
          )}
        </div>

        <Link to="/" className="btn btn-light btn-sm rounded-pill">
          <span className="d-none d-sm-inline">Back to Home</span>
          <span className="d-sm-none"><i className="bi bi-house" /></span>
        </Link>
      </nav>

      {bannerVisible && (
        <Alert
          variant="dark"
          className="rounded-0 border-0 mb-0 py-2 text-center small flex-shrink-0"
          dismissible
          onClose={() => setBannerVisible(false)}
        >
          MedHive&apos;s documentation is actively being updated and may contain typos or errors
          until finalized.
        </Alert>
      )}

      <div className="docs-body flex-grow-1 overflow-hidden">
        <Container fluid className="h-100 p-0">
          <Row className="g-0 h-100">
            <Col lg={3} xl={2} className="d-none d-lg-flex flex-column border-end bg-body-tertiary docs-sidebar bg-white h-100">
              <div className="px-3 pt-3 pb-2 small fw-semibold text-muted d-flex align-items-center gap-1 flex-shrink-0">
                Documentation
                <i className="bi bi-chevron-down" style={{ fontSize: '0.65rem' }} />
              </div>
              <div className="flex-grow-1 overflow-hidden">
                <DocsSidebarNav activeSection={activeSection} />
              </div>
              <div className="flex-shrink-0 px-3 py-3 border-top">
                <Button
                  variant="link"
                  size="sm"
                  className="text-muted text-decoration-none p-0 d-flex align-items-center gap-2"
                  onClick={() => setDarkMode((prev) => !prev)}
                >
                  <i className={`bi ${darkMode ? 'bi-sun' : 'bi-moon'}`} />
                  {darkMode ? 'Dark' : 'Light'}
                </Button>
              </div>
            </Col>

            <Col lg={9} xl={10} className="h-100 card-bg p-0">
              <div
                ref={mainRef}
                className="docs-main h-100 overflow-y-auto px-4 px-lg-5 py-4 pb-5"
              >
                <Row className="g-4 g-xl-5">
                  <Col xs={12} xl={9}>
                    <Breadcrumb className="small mb-4">
                      <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/documentation/overview' }}>
                        Documentation
                      </Breadcrumb.Item>
                      <Breadcrumb.Item active>{currentDoc.title}</Breadcrumb.Item>
                    </Breadcrumb>
                    <h1 className="display-6 fw-bold mb-4">{pageTitle}</h1>
                    {SECTION_CONTENT[activeSection]}
                  </Col>
                  <Col xs={12} xl={3} className="d-none d-xl-block">
                    <DocsOnThisPage headings={currentDoc.headings} activeHeading={activeHeading} />
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Offcanvas show={sidebarOpen} onHide={() => setSidebarOpen(false)} placement="start">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="small fw-semibold text-muted">Documentation</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="d-flex flex-column">
          <DocsSidebarNav
            activeSection={activeSection}
            onNavigate={() => setSidebarOpen(false)}
          />
          <div className="mt-auto pt-3 border-top">
            <Button
              variant="link"
              size="sm"
              className="text-muted text-decoration-none p-0 d-flex align-items-center gap-2"
              onClick={() => setDarkMode((prev) => !prev)}
            >
              <i className={`bi ${darkMode ? 'bi-sun' : 'bi-moon'}`} />
              {darkMode ? 'Dark' : 'Light'}
            </Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default DocumentationPage;
